import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
    Users, Camera, MapPin, X, CheckCircle, AlertCircle,
    Loader, LogIn, LogOut, TrendingUp, Search,
    Clock, Calendar, ChevronLeft, ChevronRight,
    LayoutList, Download, FileText, CheckSquare, XSquare, Plus, Settings, User
} from 'lucide-react';
import { toast } from 'react-toastify';
import { API_URL as API_URL_CONFIG } from '../../config';
import './Attendance.css';

const API_URL = API_URL_CONFIG;

const Attendance = () => {
    // =========================================================
    // 1. STATE MANAGEMENT
    // =========================================================
    const [currentUser, setCurrentUser] = useState(null);
    const [userRole, setUserRole] = useState('EMPLOYEE');
    const [viewMode, setViewMode] = useState('ADMIN_MASTER'); // ADMIN_MASTER, ADMIN_REQUESTS, ADMIN_LOGS, PERSONAL, ADMIN_SHIFTS

    // Data States
    const [masterAttendance, setMasterAttendance] = useState([]);
    const [personalAttendance, setPersonalAttendance] = useState([]);
    const [personalStats, setPersonalStats] = useState({ present_days: 0, absent_days: 0, late_days: 0, avg_hours: '00:00' });

    // Regularization & Shifts
    const [regRequests, setRegRequests] = useState([]);
    const [correctionLogs, setCorrectionLogs] = useState([]);
    const [shiftPolicies, setShiftPolicies] = useState([]);
    const [isRegModalOpen, setIsRegModalOpen] = useState(false);
    const [isShiftModalOpen, setIsShiftModalOpen] = useState(false);
    const [isDirectEntryOpen, setIsDirectEntryOpen] = useState(false);
    const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
    const [rejectData, setRejectData] = useState({ requestId: null, rejectionReason: '' });

    // WFH States
    const [wfhRequests, setWfhRequests] = useState([]);
    const [isWFHModalOpen, setIsWFHModalOpen] = useState(false);
    const [wfhFormData, setWfhFormData] = useState({ date: '', reason: '' });

    // Form States
    const [regFormData, setRegFormData] = useState({ date: '', punchIn: '', punchOut: '', reason: '' });
    const [shiftFormData, setShiftFormData] = useState({ id: null, policy_name: '', full_day_hours: 9, start_time: '09:00', grace_period_mins: 15 });
    const [directEntryData, setDirectEntryData] = useState({ userId: null, date: '', punchIn: '', punchOut: '', reason: '', status: '' });

    // UI States
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedUser, setSelectedUser] = useState(null);

    // Filter States
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [dateRange, setDateRange] = useState({
        start: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
        end: new Date().toISOString().split('T')[0]
    });

    // Punch States
    const [isMyClockIn, setIsMyClockIn] = useState(false);
    const [isPunchModalOpen, setIsPunchModalOpen] = useState(false);
    const [punchStep, setPunchStep] = useState(1);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [punchResult, setPunchResult] = useState({ success: false, message: '' });
    const [location, setLocation] = useState({ lat: null, lng: null, address: '', status: 'idle' });
    const [cameraStream, setCameraStream] = useState(null);
    const [cameraError, setCameraError] = useState(null);
    const [capturedImage, setCapturedImage] = useState(null);
    const [gpsConfig, setGpsConfig] = useState({ enabled: false, loading: true });
    const [todayStatus, setTodayStatus] = useState({ isWFH: false, punchCount: 0, canPunch: true });

    const videoRef = useRef(null);
    const canvasRef = useRef(null);

    // =========================================================
    // 2. INITIALIZATION
    // =========================================================
    useEffect(() => {
        const token = localStorage.getItem('zTaskAuthToken');
        if (token) {
            try {
                const payload = JSON.parse(atob(token.split('.')[1]));
                let userId = payload.user_id || payload.id || payload.userId || payload.sub;

                if (!userId || userId === 'undefined') {
                    const storedUser = JSON.parse(localStorage.getItem('zTaskUserDetails') || '{}');
                    userId = storedUser.id || storedUser.userId;
                }

                const role = (payload.roles || payload.claims?.roles || 'EMPLOYEE').toUpperCase();
                setCurrentUser({ id: userId || null, name: payload.name || 'User', role });

                const isAdmin = ['ADMIN', 'SUPER_ADMIN', 'HR'].includes(role);
                setUserRole(isAdmin ? 'ADMIN' : 'EMPLOYEE');
                setViewMode(isAdmin ? 'ADMIN_MASTER' : 'PERSONAL');

                // Initial Data Loads
                fetchMyPunchStatus(userId);
                fetchTodayStatus(userId);
                fetchGPSConfig();
                if (isAdmin) {
                    fetchMasterAttendance();
                    fetchRegRequests();
                } else {
                    fetchPersonalData(userId);
                }
            } catch (e) { console.error("Token Init Error", e); }
        }
    }, []);

    // Fetch Effect for Filters
    useEffect(() => {
        if (viewMode === 'ADMIN_MASTER') {
            fetchMasterAttendance();
        } else if (viewMode === 'ADMIN_REQUESTS') {
            fetchRegRequests();
        } else if (viewMode === 'ADMIN_LOGS') {
            fetchCorrectionLogs();
        } else if (viewMode === 'ADMIN_SHIFTS') {
            fetchShiftPolicies();
        } else if (viewMode === 'PERSONAL' && currentUser) {
            fetchPersonalData(currentUser.id);
        }
    }, [dateRange, viewMode]);

    // =========================================================
    // 3. API CALLS
    // =========================================================
    const getAuthHeaders = () => ({
        'Authorization': `Bearer ${localStorage.getItem('zTaskAuthToken')}`,
        'Content-Type': 'application/json'
    });

    // --- Tooltip State ---
    const [activeTooltip, setActiveTooltip] = useState(null);

    // --- Regularization APIs ---
    const fetchRegRequests = async () => {
        if (!currentUser) return;
        setLoading(true);
        try {
            const query = userRole === 'ADMIN' ? '?status=PENDING' : `?userId=${currentUser.id}`;
            const res = await fetch(`${API_URL}/regularization/list${query}`, { headers: getAuthHeaders() });
            if (res.ok) setRegRequests(await res.json());
        } catch (e) { console.error(e); }
        setLoading(false);
    };

    const submitRegRequest = async () => {
        if (!regFormData.date || !regFormData.reason) return toast.warning("Date and Reason are required.");

        const loadingToast = toast.loading("Submitting correction request...");
        try {
            const res = await fetch(`${API_URL}/regularization/create`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({
                    userId: currentUser.id,
                    date: regFormData.date,
                    punchInTime: regFormData.punchIn,
                    punchOutTime: regFormData.punchOut,
                    reason: regFormData.reason
                })
            });
            const data = await res.json();
            if (res.ok) {
                toast.update(loadingToast, { render: "Request Submitted Successfully!", type: "success", isLoading: false, autoClose: 3000 });
                setIsRegModalOpen(false);
                setRegFormData({ date: '', punchIn: '', punchOut: '', reason: '' });
                fetchRegRequests();
            } else {
                toast.update(loadingToast, { render: data.error || "Failed to submit request.", type: "error", isLoading: false, autoClose: 3000 });
            }
        } catch (e) {
            console.error(e);
            toast.update(loadingToast, { render: "Server Connectivity Error", type: "error", isLoading: false, autoClose: 3000 });
        }
    };

    const handleRegAction = async (requestId, action) => {
        if (action === 'REJECTED') {
            // Open rejection modal
            setRejectData({ requestId, rejectionReason: '' });
            setIsRejectModalOpen(true);
            return;
        }

        // For APPROVED action
        if (!window.confirm(`Are you sure you want to ${action} this request?`)) return;
        const loadingToast = toast.loading(`Processing ${action}...`);
        try {
            const res = await fetch(`${API_URL}/regularization/action`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({ requestId, action })
            });
            if (res.ok) {
                toast.update(loadingToast, { render: `Request ${action} successfully.`, type: "success", isLoading: false, autoClose: 3000 });
                fetchRegRequests();
                fetchMasterAttendance();
            } else {
                const err = await res.json();
                toast.update(loadingToast, { render: err.error || "Action Failed.", type: "error", isLoading: false, autoClose: 3000 });
            }
        } catch (e) {
            console.error(e);
            toast.update(loadingToast, { render: "Request Failed due to network error", type: "error", isLoading: false, autoClose: 3000 });
        }
    };

    const submitRejection = async () => {
        if (!rejectData.rejectionReason.trim()) {
            toast.warning("Please provide a reason for rejection.");
            return;
        }

        const loadingToast = toast.loading("Processing rejection...");
        try {
            const res = await fetch(`${API_URL}/regularization/action`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({
                    requestId: rejectData.requestId,
                    action: 'REJECTED',
                    rejectionReason: rejectData.rejectionReason
                })
            });
            if (res.ok) {
                toast.update(loadingToast, { render: "Request rejected successfully.", type: "success", isLoading: false, autoClose: 3000 });
                setIsRejectModalOpen(false);
                setRejectData({ requestId: null, rejectionReason: '' });
                fetchRegRequests();
                fetchMasterAttendance();
            } else {
                const err = await res.json();
                toast.update(loadingToast, { render: err.error || "Rejection Failed.", type: "error", isLoading: false, autoClose: 3000 });
            }
        } catch (e) {
            console.error(e);
            toast.update(loadingToast, { render: "Request Failed due to network error", type: "error", isLoading: false, autoClose: 3000 });
        }
    };

    const fetchCorrectionLogs = async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_URL}/regularization/logs`, { headers: getAuthHeaders() });
            if (res.ok) setCorrectionLogs(await res.json());
        } catch (e) { console.error(e); }
        setLoading(false);
    };

    // --- WFH APIs ---
    const fetchWFHRequests = async () => {
        setLoading(true);
        try {
            const query = userRole === 'ADMIN' ? '?status=PENDING' : '';
            const res = await fetch(`${API_URL}/wfh/list${query}`, { headers: getAuthHeaders() });
            if (res.ok) setWfhRequests(await res.json());
        } catch (e) { console.error(e); }
        setLoading(false);
    };

    const submitWFHRequest = async () => {
        if (!wfhFormData.date) return toast.warning("Date is required.");
        const loadingToast = toast.loading("Submitting WFH request...");
        try {
            const res = await fetch(`${API_URL}/wfh/request`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({ date: wfhFormData.date, reason: wfhFormData.reason })
            });
            const data = await res.json();
            if (res.ok) {
                toast.update(loadingToast, { render: "WFH Request Submitted!", type: "success", isLoading: false, autoClose: 3000 });
                setIsWFHModalOpen(false);
                setWfhFormData({ date: '', reason: '' });
            } else {
                toast.update(loadingToast, { render: data.error || "Failed to submit.", type: "error", isLoading: false, autoClose: 3000 });
            }
        } catch (e) {
            console.error(e);
            toast.update(loadingToast, { render: "Server Error", type: "error", isLoading: false, autoClose: 3000 });
        }
    };

    const handleWFHAction = async (requestId, action) => {
        let rejectionReason = '';
        if (action === 'REJECTED') {
            rejectionReason = window.prompt("Please provide a reason for rejection:");
            if (!rejectionReason) return;
        }
        if (action === 'APPROVED' && !window.confirm("Approve this WFH request?")) return;

        const loadingToast = toast.loading(`Processing ${action}...`);
        try {
            const res = await fetch(`${API_URL}/wfh/action`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({ requestId, action, rejectionReason })
            });
            if (res.ok) {
                toast.update(loadingToast, { render: `WFH Request ${action}!`, type: "success", isLoading: false, autoClose: 3000 });
                fetchWFHRequests();
            } else {
                const err = await res.json();
                toast.update(loadingToast, { render: err.error || "Action Failed.", type: "error", isLoading: false, autoClose: 3000 });
            }
        } catch (e) {
            console.error(e);
            toast.update(loadingToast, { render: "Network Error", type: "error", isLoading: false, autoClose: 3000 });
        }
    };

    // --- Shift Policy APIs ---
    const fetchShiftPolicies = async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_URL}/regularization/policies`, { headers: getAuthHeaders() });
            if (res.ok) setShiftPolicies(await res.json());
        } catch (e) { console.error(e); }
        setLoading(false);
    };

    const saveShiftPolicy = async () => {
        try {
            const res = await fetch(`${API_URL}/regularization/policies/upsert`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify(shiftFormData)
            });
            if (res.ok) {
                toast.success("Shift Policy Saved!");
                setIsShiftModalOpen(false);
                fetchShiftPolicies();
            } else {
                const err = await res.json();
                toast.error(err.error || "Failed to save.");
            }
        } catch (e) {
            console.error(e);
            toast.error("Internal Server Error");
        }
    };

    const assignPolicyToUser = async (userId, policyId) => {
        try {
            const res = await fetch(`${API_URL}/regularization/policies/assign`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({ userId, policyId })
            });
            if (res.ok) {
                alert("Policy Assigned Successfully!");
                fetchMasterAttendance();
            } else { alert("Failed to assign."); }
        } catch (e) { console.error(e); }
    };

    // --- Direct Entry API ---
    const submitDirectEntry = async () => {
        if (!directEntryData.userId || !directEntryData.date) return toast.error("User and Date required.");

        const loadingToast = toast.loading("Updating attendance...");
        try {
            const res = await fetch(`${API_URL}/regularization/direct-entry`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({
                    targetUserId: directEntryData.userId,
                    date: directEntryData.date,
                    punchInTime: directEntryData.punchIn,
                    punchOutTime: directEntryData.punchOut,
                    reason: directEntryData.reason,
                    status: directEntryData.status
                })
            });
            if (res.ok) {
                toast.update(loadingToast, { render: "Attendance Updated Successfully!", type: "success", isLoading: false, autoClose: 3000 });
                setIsDirectEntryOpen(false);
                fetchMasterAttendance();
            } else {
                const err = await res.json();
                toast.update(loadingToast, { render: err.error || "Failed to update.", type: "error", isLoading: false, autoClose: 3000 });
            }
        } catch (e) {
            console.error(e);
            toast.update(loadingToast, { render: "A network error occurred.", type: "error", isLoading: false, autoClose: 3000 });
        }
    };

    const fetchMyPunchStatus = async (userId) => {
        if (!userId) return;
        try {
            const res = await fetch(`${API_URL}/attendance/history?userId=${userId}`, { headers: getAuthHeaders() });
            if (res.ok) {
                const records = await res.json();
                setIsMyClockIn(records.length > 0 && records[0].punch_type === 'IN');
            }
        } catch (e) { console.error(e); }
    };

    const fetchGPSConfig = async () => {
        try {
            const res = await fetch(`${API_URL}/gps-config`, { headers: getAuthHeaders() });
            if (res.ok) {
                const data = await res.json();
                console.log("[Attendance] GPS Config Fetched:", data);
                setGpsConfig({ enabled: data.gpsEnabled, loading: false });
            } else {
                console.warn("[Attendance] GPS Config Fetch Failed (Status), defaulting to FALSE");
                setGpsConfig({ enabled: false, loading: false });
            }
        } catch (e) {
            console.error("[Attendance] GPS Config Network Error, defaulting to FALSE:", e);
            setGpsConfig({ enabled: false, loading: false });
        }
    };

    const fetchTodayStatus = async (userId) => {
        if (!userId) return;
        try {
            const res = await fetch(`${API_URL}/attendance/today-status?userId=${userId}`, { headers: getAuthHeaders() });
            if (res.ok) {
                const data = await res.json();
                setTodayStatus({
                    isWFH: data.isWFH || false,
                    punchCount: data.punchCount || 0,
                    canPunch: (data.punchCount || 0) < 6
                });
            }
        } catch (e) {
            console.error("[Attendance] Today Status Fetch Error:", e);
        }
    };

    const formatDateNice = (dateStr) => {
        if (!dateStr) return '';
        const d = new Date(dateStr);
        return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    };

    const fetchMasterAttendance = async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_URL}/attendance/all-summary?startDate=${dateRange.start}&endDate=${dateRange.end}`, { headers: getAuthHeaders() });
            if (res.ok) {
                const data = await res.json();
                setMasterAttendance(data);
            }
        } catch (e) { console.error(e); }
        setLoading(false);
    };

    const fetchPersonalData = async (userId) => {
        if (!userId) return;
        setLoading(true);
        try {
            const query = `?userId=${userId}&startDate=${dateRange.start}&endDate=${dateRange.end}`;
            const [summaryRes, statsRes] = await Promise.all([
                fetch(`${API_URL}/attendance/summary${query}`, { headers: getAuthHeaders() }),
                fetch(`${API_URL}/attendance/stats?userId=${userId}`, { headers: getAuthHeaders() })
            ]);
            if (summaryRes.ok) setPersonalAttendance(await summaryRes.json());
            if (statsRes.ok) setPersonalStats(await statsRes.json());
        } catch (e) { console.error(e); }
        setLoading(false);
    };

    // =========================================================
    // 4. HANDLERS
    // =========================================================
    const handleMonthChange = (direction) => {
        const nextDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + direction, 1);
        setCurrentMonth(nextDate);
        const start = new Date(nextDate.getFullYear(), nextDate.getMonth(), 1).toISOString().split('T')[0];
        const end = new Date(nextDate.getFullYear(), nextDate.getMonth() + 1, 0).toISOString().split('T')[0];
        setDateRange({ start, end });
    };

    const daysInMonth = useMemo(() => {
        const year = currentMonth.getFullYear();
        const month = currentMonth.getMonth();
        const date = new Date(year, month, 1);
        const days = [];
        while (date.getMonth() === month) {
            days.push(new Date(date));
            date.setDate(date.getDate() + 1);
        }
        return days;
    }, [currentMonth]);

    const attendanceMatrix = useMemo(() => {
        const groups = {};
        masterAttendance.forEach(rec => {
            if (!groups[rec.user_id]) {
                groups[rec.user_id] = {
                    user_id: rec.user_id,
                    first_name: rec.first_name,
                    last_name: rec.last_name,
                    emp_code: rec.emp_code,
                    designation_name: rec.designation_name,
                    dates: {}
                };
            }
            if (rec.date_str) {
                groups[rec.user_id].dates[rec.date_str] = {
                    status: rec.status_name,
                    in: rec.punch_in,
                    out: rec.punch_out,
                    duration: rec.duration_hrs
                };
            }
        });

        const search = searchTerm.toLowerCase();
        return Object.values(groups).filter(u =>
            u.first_name?.toLowerCase().includes(search) ||
            u.last_name?.toLowerCase().includes(search) ||
            u.emp_code?.toString().includes(search)
        );
    }, [masterAttendance, searchTerm]);

    const handleExport = async () => {
        try {
            toast.info('Preparing CSV Export...');
            const token = localStorage.getItem('zTaskAuthToken');
            const from = dateRange?.start || new Date().toISOString().slice(0, 10);
            const to = dateRange?.end || from;
            const res = await fetch(`${API_URL}/attendance/export?from=${from}&to=${to}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) throw new Error('Export failed');
            const blob = await res.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `attendance_${from}_to_${to}.csv`;
            a.click();
            window.URL.revokeObjectURL(url);
            toast.success('Attendance CSV downloaded');
        } catch (e) {
            toast.error(e.message || 'Export failed');
        }
    };

    // =========================================================
    // 6. PUNCH LOGIC
    // =========================================================
    const openPunchModal = () => {
        if (!todayStatus.canPunch) {
            toast.error("Maximum punch limit (3 pairs) reached for today.");
            return;
        }
        setIsPunchModalOpen(true);
        setPunchStep(1);
        setCapturedImage(null);
        setPunchResult({ success: false, message: '' });
        setTimeout(() => initializeHardware(), 300);
    };

    const initializeHardware = async () => {
        setLocation(p => ({ ...p, status: 'loading' }));
        setCameraError(null);

        // WFH BYPASS
        if (todayStatus.isWFH) {
            console.log("WFH Mode Active: Skipping Hardware Checks");
            setLocation({ lat: 0, lng: 0, address: "Work From Home", status: 'success' });
            return;
        }

        try {
            const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' } });
            setCameraStream(stream);
            if (videoRef.current) videoRef.current.srcObject = stream;
        } catch (err) { setCameraError("Camera Access Denied"); }

        if (gpsConfig.enabled && navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (pos) => setLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude, address: "GPS Active", status: 'success' }),
                () => setLocation(p => ({ ...p, status: 'error' })),
                { enableHighAccuracy: true, timeout: 10000 }
            );
        } else if (!gpsConfig.enabled) {
            setLocation({ lat: 0, lng: 0, address: "GPS Disabled by Admin", status: 'success' });
        } else {
            setLocation(p => ({ ...p, status: 'error' }));
        }
    };

    const closePunchModal = () => {
        if (cameraStream) cameraStream.getTracks().forEach(t => t.stop());
        setIsPunchModalOpen(false);
    };

    const capturePhoto = () => {
        if (videoRef.current && canvasRef.current) {
            const video = videoRef.current;
            const canvas = canvasRef.current;
            const ctx = canvas.getContext('2d');
            
            // Use video dimensions or fallback to 640x480
            const width = video.videoWidth || 640;
            const height = video.videoHeight || 480;
            
            canvas.width = width;
            canvas.height = height;
            ctx.drawImage(video, 0, 0, width, height);
            setCapturedImage(canvas.toDataURL('image/jpeg', 0.8));
            setPunchStep(2);
        } else {
            console.error("capturePhoto: videoRef or canvasRef is null");
            toast.error("Camera not ready. Please try again.");
        }
    }

    const submitPunch = async () => {
        setIsSubmitting(true);
        if (!currentUser || !currentUser.id || currentUser.id === 'undefined') {
            setPunchResult({ success: false, message: "User identification missing." });
            setIsSubmitting(false);
            setPunchStep(3);
            return;
        }
        setIsSubmitting(false);
        setPunchStep(3);
    };

    const StatusBadge = ({ status, compact = false }) => {
        if (!status || status === '-' || status === 'No Record') {
            return <span className="status-dash">-</span>;
        }

        const s = status.toLowerCase();
        let colorClass = 'status-default';
        let abbreviation = status.charAt(0).toUpperCase();
        let fullLabel = status;

        if (s.includes('present') || s === 'p') {
            colorClass = 'status-success';
            abbreviation = 'P';
            fullLabel = 'Present';
        } else if (s.includes('absent') || s === 'a') {
            colorClass = 'status-danger';
            abbreviation = 'A';
            fullLabel = 'Absent';
        } else if (s.includes('late')) {
            colorClass = 'status-warning';
            abbreviation = 'L';
            fullLabel = 'Late Arrival';
        } else if (s.includes('half')) {
            colorClass = 'status-warning';
            abbreviation = 'H';
            fullLabel = 'Half Day';
        } else if (s.includes('insufficient') || s.includes('short')) {
            colorClass = 'status-danger';
            abbreviation = 'S';
            fullLabel = 'Short Day';
        }

        if (compact) {
            return (
                <div className={`compact-circle-badge ${colorClass}`}>
                    {abbreviation}
                </div>
            );
        }

        return (
            <div className={`premium-badge-status ${colorClass}`}>
                {fullLabel}
            </div>
        );
    };

    // =========================================================
    // 8. RENDER VIEWS
    // =========================================================

    // --- RENDER SHIFT MANAGEMENT ---
    const renderShiftManagement = () => (
        <div className="attendance-content-scrollable">
            <div className="premium-header-actions">
                <h2>Shift Policies</h2>
                <button className="btn-export" onClick={() => {
                    setShiftFormData({ id: null, policy_name: '', full_day_hours: 9, start_time: '09:00', grace_period_mins: 15 });
                    setIsShiftModalOpen(true);
                }}>
                    <Plus size={16} /> Create Policy
                </button>
            </div>
            <div className="master-table-card">
                <table className="dense-table">
                    <thead>
                        <tr>
                            <th>Policy Name</th>
                            <th>Full Day Hrs</th>
                            <th>Start Time</th>
                            <th>End Time (Calc)</th>
                            <th>Grace Mins</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {shiftPolicies.map(p => {
                            // Calculate End Time
                            let endTime = '-';
                            if (p.start_time && p.full_day_hours) {
                                const [h, m] = p.start_time.split(':').map(Number);
                                const d = new Date();
                                d.setHours(h, m, 0);
                                d.setTime(d.getTime() + (p.full_day_hours * 60 * 60 * 1000));
                                endTime = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
                            }
                            return (
                                <tr key={p.id}>
                                    <td className="user-name-bold">{p.policy_name}</td>
                                    <td>{p.full_day_hours} hrs</td>
                                    <td>{p.start_time}</td>
                                    <td>{endTime}</td>
                                    <td>{p.grace_period_mins} mins</td>
                                    <td>
                                        <button className="btn-view" onClick={() => {
                                            setShiftFormData(p);
                                            setIsShiftModalOpen(true);
                                        }}>Edit</button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );

    const renderWFHRequests = () => (
        <div className="attendance-master-view">
            <div className="premium-header-actions">
                <div className="section-title">
                    <span style={{ fontSize: '20px' }}>🏠</span>
                    <h3>Work From Home Requests</h3>
                </div>
                <button className="btn-export" onClick={fetchWFHRequests}>
                    <Loader size={16} className={loading ? "spin" : ""} /> Refresh
                </button>
            </div>
            <div className="attendance-grid-container">
                <div className="grid-table-wrapper">
                    <table className="attendance-matrix-table">
                        <thead>
                            <tr>
                                <th className="text-left" style={{ paddingLeft: '24px' }}>Employee</th>
                                <th className="text-left">Role</th>
                                <th className="text-left">Date</th>
                                <th className="text-left">Reason</th>
                                <th className="text-center">Status</th>
                                <th className="text-left">Approved By</th>
                                <th className="text-center">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {wfhRequests.length > 0 ? (
                                wfhRequests.map(req => (
                                    <tr key={req.id}>
                                        <td className="text-left" style={{ paddingLeft: '24px' }}>
                                            <div className="user-profile-cell">
                                                <div className="user-avatar-sm">{(req.first_name || 'U').charAt(0)}</div>
                                                <div className="user-meta">
                                                    <span className="user-name-bold">{req.first_name} {req.last_name}</span>
                                                    <span className="user-id-sub">#{req.emp_code}</span>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="text-left">{req.requester_role}</td>
                                        <td className="text-left">{new Date(req.request_date).toLocaleDateString('en-GB')}</td>
                                        <td className="reason-cell text-left">{req.reason || '-'}</td>
                                        <td className="text-center">
                                            <span className={`badge-p-a status-${req.status === 'APPROVED' ? 'success' : req.status === 'REJECTED' ? 'danger' : 'warning'}`} style={{ width: 'auto', padding: '0 12px', borderRadius: '20px' }}>
                                                {req.status}
                                            </span>
                                        </td>
                                        <td className="text-left">
                                            {req.approver_first_name ? `${req.approver_first_name} ${req.approver_last_name}` : '-'}
                                        </td>
                                        <td className="text-center">
                                            {req.status === 'PENDING' && (
                                                <div className="action-buttons-row">
                                                    <button className="btn-icon-success" onClick={() => handleWFHAction(req.id, 'APPROVED')} title="Approve">
                                                        <CheckSquare size={18} />
                                                    </button>
                                                    <button className="btn-icon-danger" onClick={() => handleWFHAction(req.id, 'REJECTED')} title="Reject">
                                                        <XSquare size={18} />
                                                    </button>
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr><td colSpan="7" className="empty-state-row">No WFH requests found.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );

    const renderRequestsList = () => (
        <div className="attendance-master-view">
            <div className="premium-header-actions">
                <div className="section-title">
                    <FileText size={20} />
                    <h3>Regularization Requests</h3>
                </div>
                <button className="btn-export" onClick={fetchRegRequests}>
                    <Loader size={16} className={loading ? "spin" : ""} /> Refresh
                </button>
            </div>
            <div className="attendance-grid-container">
                <div className="grid-table-wrapper">
                    <table className="attendance-matrix-table">
                        <thead>
                            <tr>
                                <th className="text-left" style={{ paddingLeft: '24px' }}>Employee</th>
                                <th className="text-left">Date</th>
                                <th className="text-left">Corrected In</th>
                                <th className="text-left">Corrected Out</th>
                                <th className="text-left">Reason</th>
                                <th className="text-center">Status</th>
                                <th className="text-center">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {regRequests.length > 0 ? (
                                regRequests.map(req => (
                                    <tr key={req.id}>
                                        <td className="text-left" style={{ paddingLeft: '24px' }}>
                                            <div className="user-profile-cell">
                                                <div className="user-avatar-sm">{(req.first_name || 'U').charAt(0)}</div>
                                                <div className="user-meta">
                                                    <span className="user-name-bold">{req.first_name} {req.last_name}</span>
                                                    <span className="user-id-sub">#{req.emp_code}</span>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="text-left">{new Date(req.request_date).toLocaleDateString('en-GB')}</td>
                                        <td className="text-left">{req.corrected_punch_in ? new Date(req.corrected_punch_in).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '-'}</td>
                                        <td className="text-left">{req.corrected_punch_out ? new Date(req.corrected_punch_out).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '-'}</td>
                                        <td className="reason-cell text-left">{req.reason}</td>
                                        <td className="text-center">
                                            <span className={`badge-p-a status-${req.status.toLowerCase() === 'pending' ? 'warning' : req.status.toLowerCase() === 'approved' ? 'success' : 'danger'}`} style={{ width: 'auto', padding: '0 12px', borderRadius: '20px' }}>
                                                {req.status}
                                            </span>
                                        </td>
                                        <td className="text-center">
                                            {req.status === 'PENDING' && (
                                                <div className="action-buttons-row">
                                                    <button className="btn-icon-success" onClick={() => handleRegAction(req.id, 'APPROVED')} title="Approve">
                                                        <CheckSquare size={18} />
                                                    </button>
                                                    <button className="btn-icon-danger" onClick={() => handleRegAction(req.id, 'REJECTED')} title="Reject">
                                                        <XSquare size={18} />
                                                    </button>
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr><td colSpan="7" className="empty-state-row">No pending requests found.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );

    const renderCorrectionLogs = () => (
        <div className="attendance-master-view">
            <div className="premium-header-actions">
                <div className="section-title">
                    <CheckCircle size={20} />
                    <h3>Correction Logs (Processed Requests)</h3>
                </div>
                <button className="btn-export" onClick={fetchCorrectionLogs}>
                    <Loader size={16} className={loading ? "spin" : ""} /> Refresh
                </button>
            </div>
            <div className="attendance-grid-container">
                <div className="grid-table-wrapper">
                    <table className="attendance-matrix-table">
                        <thead>
                            <tr>
                                <th className="text-left" style={{ paddingLeft: '24px' }}>Employee</th>
                                <th className="text-left">Date</th>
                                <th className="text-left">Corrected In</th>
                                <th className="text-left">Corrected Out</th>
                                <th className="text-left">Reason</th>
                                <th className="text-center">Status</th>
                                <th className="text-left">Approved By</th>
                                <th className="text-left">Action Date</th>
                            </tr>
                        </thead>
                        <tbody>
                            {correctionLogs.length > 0 ? (
                                correctionLogs.map(log => (
                                    <tr key={log.id}>
                                        <td className="text-left" style={{ paddingLeft: '24px' }}>
                                            <div className="user-profile-cell">
                                                <div className="user-avatar-sm">{(log.first_name || 'U').charAt(0)}</div>
                                                <div className="user-meta">
                                                    <span className="user-name-bold">{log.first_name} {log.last_name}</span>
                                                    <span className="user-id-sub">#{log.emp_code} • {log.requester_role}</span>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="text-left">{new Date(log.request_date).toLocaleDateString('en-GB')}</td>
                                        <td className="text-left">{log.corrected_punch_in ? new Date(log.corrected_punch_in).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '-'}</td>
                                        <td className="text-left">{log.corrected_punch_out ? new Date(log.corrected_punch_out).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '-'}</td>
                                        <td className="reason-cell text-left">{log.reason}</td>
                                        <td className="text-center">
                                            <span className={`badge-p-a status-${log.status.toLowerCase() === 'approved' ? 'success' : 'danger'}`} style={{ width: 'auto', padding: '0 12px', borderRadius: '20px' }}>
                                                {log.status}
                                            </span>
                                            {log.status === 'REJECTED' && log.rejection_reason && (
                                                <div style={{ marginTop: '4px', fontSize: '12px', color: '#ef4444', fontStyle: 'italic' }}>
                                                    Reason: {log.rejection_reason}
                                                </div>
                                            )}
                                        </td>
                                        <td className="text-left">
                                            <div className="user-meta">
                                                <span className="user-name-bold">{log.approved_by_name || 'N/A'}</span>
                                                <span className="user-id-sub">{log.approver_role}</span>
                                            </div>
                                        </td>
                                        <td className="text-left">{log.admin_action_at ? new Date(log.admin_action_at).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' }) : '-'}</td>
                                    </tr>
                                ))
                            ) : (
                                <tr><td colSpan="8" className="empty-state-row">No correction logs found.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );

    const renderMasterList = () => (
        <div className="attendance-master-view">
            <div className="premium-header-actions">
                <div className="search-box-premium">
                    <Search size={18} />
                    <input
                        type="text"
                        placeholder="Search employee, designation, or code..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
                <div className="filter-controls">
                    <div className="month-picker-premium">
                        <button onClick={() => handleMonthChange(-1)}><ChevronLeft size={20} /></button>
                        <span>{currentMonth.toLocaleDateString('default', { month: 'long', year: 'numeric' })}</span>
                        <button onClick={() => handleMonthChange(1)}><ChevronRight size={20} /></button>
                    </div>
                    <button className="btn-export" onClick={handleExport}>
                        <Download size={16} /> Export CSV
                    </button>
                </div>
            </div>

            <div className="attendance-grid-container">
                <div className="grid-table-wrapper">
                    <table className="attendance-matrix-table">
                        <thead>
                            <tr>
                                <th className="sticky-col col-name">Employee</th>
                                {daysInMonth.map(d => (
                                    <th key={d.getDate()} className="date-col">
                                        <span className="day-num">{d.getDate()}</span>
                                        <span className="day-name">{d.toLocaleDateString('en-US', { weekday: 'short' })}</span>
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan={daysInMonth.length + 1} className="loading-state-row"><Loader className="spin" /> Syncing data...</td></tr>
                            ) : attendanceMatrix.length > 0 ? (
                                attendanceMatrix.map((user) => (
                                    <tr key={user.user_id}>
                                        <td className="sticky-col col-name">
                                            <div className="user-profile-cell">
                                                <div className="user-avatar-sm">{(user.first_name || 'U').charAt(0)}</div>
                                                <div className="user-meta">
                                                    <span className="user-name-bold">{user.first_name}</span>
                                                    <span className="user-id-sub">#{user.emp_code}</span>
                                                </div>
                                            </div>
                                        </td>
                                        {daysInMonth.map(day => {
                                            // Fix: Use local date to avoid timezone shift (e.g. 13th showing as 12th UTC)
                                            const dStr = `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, '0')}-${String(day.getDate()).padStart(2, '0')}`;
                                            const dayData = user.dates[dStr];
                                            const isFuture = day > new Date();

                                            // Determine display status
                                            let displayStatus = dayData ? dayData.status : (isFuture ? '-' : 'Absent');
                                            if (displayStatus === 'Absent' && !dayData) displayStatus = '-';

                                            return (
                                                <td key={dStr} className="status-cell cursor-pointer" onClick={() => {
                                                    setDirectEntryData({ userId: user.user_id, date: dStr, punchIn: '', punchOut: '' });
                                                    setIsDirectEntryOpen(true);
                                                }}>
                                                    <div className="status-with-tooltip"
                                                        onMouseEnter={(e) => {
                                                            if (!dayData) return;
                                                            const rect = e.currentTarget.getBoundingClientRect();
                                                            setActiveTooltip({
                                                                x: rect.left + rect.width / 2,
                                                                y: rect.top,
                                                                data: dayData
                                                            });
                                                        }}
                                                        onMouseLeave={() => setActiveTooltip(null)}
                                                    >
                                                        <StatusBadge status={displayStatus} compact={true} />
                                                    </div>
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))
                            ) : (
                                <tr><td colSpan={daysInMonth.length + 1} className="empty-state-row">No employees found matching your search.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );

    // =========================================================
    // 9. MAIN RETURN
    // =========================================================
    return (
        <div className="attendance-universe">
            {/* Top Navigation Bar */}
            <div className="attendance-top-navbar">
                <div className="navbar-title">
                    <LayoutList size={22} className="title-icon" />
                    <h1>Attendance Management</h1>
                </div>

                <div className="navbar-right">
                    {(userRole === 'ADMIN' || userRole === 'SUPER_ADMIN' || userRole === 'HR') ? (
                        <>
                            <div className="view-switcher-tabs">
                                <button className={`tab-btn ${viewMode === 'ADMIN_MASTER' ? 'active' : ''}`} onClick={() => setViewMode('ADMIN_MASTER')}>
                                    <Users size={14} /> Master List
                                </button>
                                <button className={`tab-btn ${viewMode === 'ADMIN_REQUESTS' ? 'active' : ''}`} onClick={() => setViewMode('ADMIN_REQUESTS')}>
                                    <FileText size={14} /> Requests {regRequests.length > 0 && <span className="notification-dot"></span>}
                                </button>
                                <button className={`tab-btn ${viewMode === 'ADMIN_LOGS' ? 'active' : ''}`} onClick={() => setViewMode('ADMIN_LOGS')}>
                                    <CheckCircle size={14} /> Logs
                                </button>
                                <button className={`tab-btn ${viewMode === 'ADMIN_SHIFTS' ? 'active' : ''}`} onClick={() => setViewMode('ADMIN_SHIFTS')}>
                                    <Settings size={14} /> Shifts
                                </button>
                                <button className={`tab-btn ${viewMode === 'ADMIN_WFH' ? 'active' : ''}`} onClick={() => { setViewMode('ADMIN_WFH'); fetchWFHRequests(); }}>
                                    🏠 WFH Requests {wfhRequests.filter(r => r.status === 'PENDING').length > 0 && <span className="notification-dot"></span>}
                                </button>
                                <button className={`tab-btn ${viewMode === 'PERSONAL' ? 'active' : ''}`} onClick={() => setViewMode('PERSONAL')}>
                                    <User size={14} /> My Stats
                                </button>
                            </div>
                            <button className="btn-outline-secondary" onClick={() => setIsRegModalOpen(true)} style={{ marginLeft: '12px' }}>
                                <FileText size={16} /> Request Correction
                            </button>
                        </>
                    ) : (
                        <>
                            <div className="view-switcher-tabs">
                                <button className={`tab-btn ${viewMode === 'PERSONAL' ? 'active' : ''}`} onClick={() => setViewMode('PERSONAL')}>
                                    <User size={14} /> My Stats
                                </button>
                                <button className={`tab-btn ${viewMode === 'ADMIN_LOGS' ? 'active' : ''}`} onClick={() => setViewMode('ADMIN_LOGS')}>
                                    <CheckCircle size={14} /> My Logs
                                </button>
                            </div>
                            <button className="btn-outline-secondary" onClick={() => setIsRegModalOpen(true)} style={{ marginLeft: '12px' }}>
                                <FileText size={16} /> Request Correction
                            </button>
                        </>
                    )}

                    <button className={`premium-punch-btn ${isMyClockIn ? 'out' : 'in'}`} onClick={openPunchModal} style={{ marginLeft: '12px' }}>
                        <div className="punch-dot"></div>
                        <span>{isMyClockIn ? 'Clock Out' : 'Clock In'}</span>
                        {isMyClockIn ? <LogOut size={16} /> : <LogIn size={16} />}
                    </button>

                    <button 
                        className="premium-punch-btn wfh" 
                        onClick={() => setIsWFHModalOpen(true)} 
                        style={{ marginLeft: '12px', background: 'linear-gradient(135deg, #6366f1, #8b5cf6)' }}
                    >
                        <span>🏠 Request WFH</span>
                    </button>
                </div>
            </div>

            {/* Quick Stats Banner (Only for Personal View) */}
            {viewMode === 'PERSONAL' && (
                <div className="personal-stats-banner">
                    <div className="banner-card">
                        <div className="card-icon check"><CheckCircle size={20} /></div>
                        <div className="card-values">
                            <span className="card-label">Present</span>
                            <span className="card-main">{personalStats.present_days} Days</span>
                        </div>
                    </div>
                    <div className="banner-card">
                        <div className="card-icon x"><X size={20} /></div>
                        <div className="card-values">
                            <span className="card-label">Absent</span>
                            <span className="card-main">{personalStats.absent_days} Days</span>
                        </div>
                    </div>
                    <div className="banner-card">
                        <div className="card-icon clock"><Clock size={20} /></div>
                        <div className="card-values">
                            <span className="card-label">Late arrivals</span>
                            <span className="card-main">{personalStats.late_days}</span>
                        </div>
                    </div>
                    <div className="banner-card">
                        <div className="card-icon trend"><TrendingUp size={20} /></div>
                        <div className="card-values">
                            <span className="card-label">Avg Hours</span>
                            <span className="card-main">{personalStats.avg_hours}</span>
                        </div>
                    </div>
                </div>
            )}

            {/* Main Content Area */}
            <div className="attendance-content-scrollable">
                {viewMode === 'ADMIN_MASTER' && renderMasterList()}
                {viewMode === 'ADMIN_REQUESTS' && renderRequestsList()}
                {viewMode === 'ADMIN_LOGS' && renderCorrectionLogs()}
                {viewMode === 'ADMIN_SHIFTS' && renderShiftManagement()}
                {viewMode === 'ADMIN_WFH' && renderWFHRequests()}
                {viewMode === 'PERSONAL' && (
                    <div className="personal-logs-view">
                        <div className="logs-header">
                            <h3>My Attendance Logs</h3>
                            <div className="month-picker-premium">
                                <button onClick={() => handleMonthChange(-1)}><ChevronLeft size={20} /></button>
                                <span>{currentMonth.toLocaleDateString('default', { month: 'long', year: 'numeric' })}</span>
                                <button onClick={() => handleMonthChange(1)}><ChevronRight size={20} /></button>
                            </div>
                        </div>
                        <div className="master-table-card">
                            <table className="dense-table">
                                <thead>
                                    <tr>
                                        <th className="col-date">Date</th>
                                        <th className="col-punch">Punch In</th>
                                        <th className="col-punch">Punch Out</th>
                                        <th className="col-duration">Duration</th>
                                        <th className="col-location">Location</th>
                                        <th className="col-status text-center">Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading ? (
                                        <tr><td colSpan="6" className="loading-state-row"><Loader className="spin" /> Syncing logs...</td></tr>
                                    ) : personalAttendance.length > 0 ? (
                                        personalAttendance.map((rec, id) => (
                                            <tr key={id}>
                                                <td className="date-text bold">{rec.date_display}</td>
                                                <td className="time-text text-success">
                                                    <div className="time-badge in"><LogIn size={12} /> {rec.punch_in || '--:--'}</div>
                                                </td>
                                                <td className="time-text text-danger">
                                                    <div className="time-badge out"><LogOut size={12} /> {rec.punch_out || '--:--'}</div>
                                                </td>
                                                <td className="duration-text">{rec.duration_hrs ? `${rec.duration_hrs} hrs` : '0 hrs'}</td>
                                                <td className="loc-text-dim"><MapPin size={12} /> {rec.location_address?.substring(0, 30) || 'GPS Active'}...</td>
                                                <td className="text-center"><StatusBadge status={rec.status_name} /></td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr><td colSpan="6" className="empty-state-row">No records for this month yet.</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>

            {/* Regularization Request Modal */}
            {isRegModalOpen && (
                <div className="modern-punch-overlay">
                    <div className="modern-punch-modal">
                        <div className="modal-top-bar">
                            <h3>Request Attendance Correction</h3>
                            <button onClick={() => setIsRegModalOpen(false)}><X size={20} /></button>
                        </div>
                        <div className="modal-main-content">
                            <div className="reg-form-group">
                                <label>Date</label>
                                <input
                                    type="date"
                                    className="form-control"
                                    value={regFormData.date}
                                    onChange={e => setRegFormData({ ...regFormData, date: e.target.value })}
                                />
                            </div>
                            <div className="reg-row">
                                <div className="reg-form-group">
                                    <label>Correct In Time</label>
                                    <input
                                        type="time"
                                        className="form-control"
                                        value={regFormData.punchIn}
                                        onChange={e => setRegFormData({ ...regFormData, punchIn: e.target.value })}
                                    />
                                </div>
                                <div className="reg-form-group">
                                    <label>Correct Out Time</label>
                                    <input
                                        type="time"
                                        className="form-control"
                                        value={regFormData.punchOut}
                                        onChange={e => setRegFormData({ ...regFormData, punchOut: e.target.value })}
                                    />
                                </div>
                            </div>
                            <div className="reg-form-group">
                                <label>Reason</label>
                                <textarea
                                    className="form-control"
                                    rows="3"
                                    placeholder="e.g. Forgot to punch out due to meeting..."
                                    value={regFormData.reason}
                                    onChange={e => setRegFormData({ ...regFormData, reason: e.target.value })}
                                ></textarea>
                            </div>
                            <button className="btn-action-punch in" onClick={submitRegRequest}>
                                Submit Request
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Rejection Reason Modal */}
            {isRejectModalOpen && (
                <div className="modern-punch-overlay">
                    <div className="modern-punch-modal">
                        <div className="modal-top-bar">
                            <h3>Reject Request</h3>
                            <button onClick={() => setIsRejectModalOpen(false)}><X size={20} /></button>
                        </div>
                        <div className="modal-main-content">
                            <div className="reg-form-group">
                                <label>Reason for Rejection</label>
                                <textarea
                                    className="form-control"
                                    rows="3"
                                    placeholder="Please provide a reason for rejection..."
                                    value={rejectData.rejectionReason}
                                    onChange={e => setRejectData({ ...rejectData, rejectionReason: e.target.value })}
                                ></textarea>
                            </div>
                            <div className="multi-action-row" style={{ marginTop: '20px' }}>
                                <button className="btn-outline-secondary" onClick={() => setIsRejectModalOpen(false)}>Cancel</button>
                                <button className="btn-action-punch out" onClick={submitRejection}>
                                    Confirm Rejection
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Punch Modal */}
            {isPunchModalOpen && (
                <div className="modern-punch-overlay">
                    <div className="modern-punch-modal">
                        <div className="modal-top-bar">
                            <h3>{isMyClockIn ? 'Complete your session' : 'Begin your session'}</h3>
                            <button onClick={closePunchModal}><X size={20} /></button>
                        </div>
                        <div className="modal-main-content">
                            {punchStep === 1 && (
                                <div className="step-container">
                                    <div className="video-capture-frame">
                                        <video ref={videoRef} autoPlay playsInline muted />
                                        {cameraError && <div className="camera-err-notice"><AlertCircle size={24} /><p>{cameraError}</p></div>}
                                        {gpsConfig.enabled && (
                                            <div className="gps-lock-tag">
                                                <div className={`dot ${location.status}`}></div>
                                                {location.status === 'success' ? 'GPS Locked' : 'Searching GPS...'}
                                            </div>
                                        )}
                                    </div>
                                    <button
                                        className={`btn-action-punch ${isMyClockIn ? 'out' : 'in'}`}
                                        onClick={capturePhoto}
                                        disabled={location.status !== 'success'}
                                    >
                                        <Camera size={20} /> {isMyClockIn ? 'Punch Out' : 'Punch In'}
                                    </button>
                                </div>
                            )}
                            {punchStep === 2 && (
                                <div className="step-container">
                                    <img src={capturedImage} alt="Selfie" className="preview-image-punch" />
                                    <div className="multi-action-row">
                                        <button className="btn-outline-secondary" onClick={() => { setPunchStep(1); initializeHardware(); }}>Retake</button>
                                        <button className={`btn-action-punch ${isMyClockIn ? 'out' : 'in'}`} onClick={submitPunch} disabled={isSubmitting}>
                                            {isSubmitting ? <Loader className="spin" /> : 'Confirm & Punch'}
                                        </button>
                                    </div>
                                </div>
                            )}
                            {punchStep === 3 && (
                                <div className="step-container result-area">
                                    <div className={`status-icon-giant ${punchResult.success ? 'success' : 'error'}`}>
                                        {punchResult.success ? <CheckCircle size={64} /> : <AlertCircle size={64} />}
                                    </div>
                                    <h2 className="result-title">{punchResult.success ? 'Time Logged!' : 'Failed to Punch'}</h2>
                                    <p className="result-msg">{punchResult.message}</p>
                                    <button className="btn-full-secondary" onClick={closePunchModal}>Done</button>
                                </div>
                            )}
                        </div>
                        <canvas ref={canvasRef} style={{ display: 'none' }} />
                    </div>
                </div>
            )}
            {isShiftModalOpen && (
                <div className="modern-punch-overlay">
                    <div className="modern-punch-modal">
                        <div className="modal-top-bar">
                            <h3>{shiftFormData.id ? 'Edit' : 'Create'} Shift Policy</h3>
                            <button onClick={() => setIsShiftModalOpen(false)}><X size={20} /></button>
                        </div>
                        <div className="modal-main-content">
                            <div className="reg-form-group">
                                <label>Policy Name</label>
                                <input type="text" className="form-control" value={shiftFormData.policy_name} onChange={e => setShiftFormData({ ...shiftFormData, policy_name: e.target.value })} placeholder="General Shift" />
                            </div>
                            <div className="reg-row">
                                <div className="reg-form-group">
                                    <label>Full Day Hrs</label>
                                    <input type="number" className="form-control" value={shiftFormData.full_day_hours} onChange={e => setShiftFormData({ ...shiftFormData, full_day_hours: e.target.value })} />
                                </div>
                                <div className="reg-form-group">
                                    <label>Grace Period (Mins)</label>
                                    <input type="number" className="form-control" value={shiftFormData.grace_period_mins} onChange={e => setShiftFormData({ ...shiftFormData, grace_period_mins: e.target.value })} />
                                </div>
                            </div>
                            <div className="reg-form-group">
                                <label>Start Time</label>
                                <input type="time" className="form-control" value={shiftFormData.start_time} onChange={e => setShiftFormData({ ...shiftFormData, start_time: e.target.value })} />
                            </div>
                            <button className="btn-action-punch in" style={{ marginTop: '20px' }} onClick={saveShiftPolicy}>Save Policy</button>
                        </div>
                    </div>
                </div>
            )}

            {isDirectEntryOpen && (
                <div className="modern-punch-overlay">
                    <div className="modern-punch-modal">
                        <div className="modal-top-bar">
                            <h3>Direct Attendance Entry</h3>
                            <button onClick={() => setIsDirectEntryOpen(false)}><X size={20} /></button>
                        </div>
                        <div className="modal-main-content">
                            <p style={{ marginBottom: '15px', fontSize: '14px', color: '#64748b' }}>Manual entry for user ID: <strong>{directEntryData.userId}</strong> on <strong>{formatDateNice(directEntryData.date)}</strong></p>
                            
                            <div className="reg-form-group" style={{ marginBottom: '15px' }}>
                                <label>Status</label>
                                <select 
                                    className="form-control" 
                                    value={directEntryData.status} 
                                    onChange={e => setDirectEntryData({ ...directEntryData, status: e.target.value })}
                                >
                                    <option value="">-- Select Status (or use times below) --</option>
                                    <option value="Present">Present</option>
                                    <option value="Absent">Absent</option>
                                    <option value="Half Day">Half Day</option>
                                    <option value="Late Arrival">Late Arrival</option>
                                    <option value="On Leave">On Leave</option>
                                    <option value="Holiday">Holiday</option>
                                    <option value="Week Off">Week Off</option>
                                </select>
                            </div>

                            <p style={{ fontSize: '12px', color: '#94a3b8', marginBottom: '10px', textAlign: 'center' }}>— OR set punch times manually —</p>
                            
                            <div className="reg-row">
                                <div className="reg-form-group">
                                    <label>Punch In</label>
                                    <input type="time" className="form-control" value={directEntryData.punchIn} onChange={e => setDirectEntryData({ ...directEntryData, punchIn: e.target.value })} disabled={!!directEntryData.status} />
                                </div>
                                <div className="reg-form-group">
                                    <label>Punch Out</label>
                                    <input type="time" className="form-control" value={directEntryData.punchOut} onChange={e => setDirectEntryData({ ...directEntryData, punchOut: e.target.value })} disabled={!!directEntryData.status} />
                                </div>
                            </div>
                            <button className="btn-action-punch in" style={{ marginTop: '20px' }} onClick={submitDirectEntry}>Update Attendance</button>
                        </div>
                    </div>
                </div>
            )}

            {/* WFH Request Modal */}
            {isWFHModalOpen && (
                <div className="modern-punch-overlay">
                    <div className="modern-punch-modal">
                        <div className="modal-top-bar">
                            <h3>🏠 Request Work From Home</h3>
                            <button onClick={() => setIsWFHModalOpen(false)}><X size={20} /></button>
                        </div>
                        <div className="modal-main-content">
                            <p style={{ marginBottom: '15px', fontSize: '14px', color: '#64748b' }}>
                                Submit a WFH request for approval. Your request will be reviewed by HR/Admin based on hierarchy.
                            </p>
                            <div className="reg-form-group">
                                <label>Date</label>
                                <input 
                                    type="date" 
                                    className="form-control" 
                                    value={wfhFormData.date} 
                                    min={new Date().toISOString().split('T')[0]}
                                    onChange={e => setWfhFormData({ ...wfhFormData, date: e.target.value })} 
                                />
                            </div>
                            <div className="reg-form-group">
                                <label>Reason (Optional)</label>
                                <textarea 
                                    className="form-control" 
                                    rows="3"
                                    placeholder="Why do you need to work from home?"
                                    value={wfhFormData.reason} 
                                    onChange={e => setWfhFormData({ ...wfhFormData, reason: e.target.value })} 
                                />
                            </div>
                            <button className="btn-action-punch in" style={{ marginTop: '20px' }} onClick={submitWFHRequest}>
                                Submit WFH Request
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {activeTooltip && activeTooltip.data && (
                <div
                    className="fixed-tooltip"
                    style={{
                        top: activeTooltip.y - 12 + 'px',
                        left: activeTooltip.x + 'px',
                        transform: 'translate(-50%, -100%)'
                    }}
                >
                    <div className="tooltip-row"><strong>Status:</strong> {activeTooltip.data.status}</div>
                    <div className="tooltip-row"><strong>In:</strong> {activeTooltip.data.in || '--:--'}</div>
                    <div className="tooltip-row"><strong>Out:</strong> {activeTooltip.data.out || '--:--'}</div>
                    <div className="tooltip-row"><strong>Work:</strong> {activeTooltip.data.duration || 0} hrs</div>
                </div>
            )}
        </div>
    );
};

export default Attendance;